<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import { APP_EVENTS, appEventBus } from '@/Utils/appEventBus'
import UserService from '@/services/UserService'
import { track, EVENTS } from '@/lib/tracking/index'
import { useTeamMembers } from '@/composables/useTeamMembers'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEditPencil from '@/components/icon/IconEditPencil.vue'
import IconTeam from '@/components/icon/IconTeam.vue'
import TeamTable from '@/components/Team/TeamTable.vue'
import TeamSeatUsage from '@/components/Team/TeamSeatUsage.vue'
import TeamRemoveDialog from '@/components/Team/TeamRemoveDialog.vue'
import TeamMemberModal from '@/components/modals/TeamMemberModal.vue'
import ProjectTile from '@/components/project/ProjectTile.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()

const {
	members,
	teamName,
	tid,
	loading: membersLoading,
	loaded,
	memberLimit,
	teamAllowed,
	seatUsage,
	activeProject,
	load,
	upsertMember,
	removeById,
	applyProjectRemoval,
	refreshInvited,
	refreshSeats,
} = useTeamMembers()

/**
 * One team per account; access is granted per workspace. Saying so only helps
 * once there is more than one workspace to tell apart — a single-project account
 * would just see unexplained jargon.
 */
const hasMultipleProjects = computed(() => store.getters['Projects/hasMultipleProjects'])
const showProjectContext = computed(() => !!activeProject.value && hasMultipleProjects.value)
const headerSubtitle = computed(() => (
	showProjectContext.value
		? `One team for your whole account. Roles and access below are for ${activeProject.value.name}; switch workspaces to manage the others.`
		: 'Control who can access your dashboard and exactly what they can do.'
))

// --- UI state ----------------------------------------------------------------
const search = ref('')
const statusFilter = ref('all') // all | active | pending | paused
const showCreateForm = ref(false)
const teamnameDraft = ref('')
const createError = ref('')
const editingName = ref(false)
const nameDraft = ref('')
const permissionTogglestatus = ref(false)
const modalMember = ref(null)
// "Add <member> to <workspace>" from a row pre-fills the invite sheet.
const modalPrefill = ref(null)

// --- remove / cancel confirmation --------------------------------------------
const removeDialogOpen = ref(false)
const removeTarget = ref(null) // { member, scope: 'project' | 'account' }
const removing = ref(false)
const removeError = ref('')

// --- store-derived -----------------------------------------------------------
const userloaded = computed(() => store.state.User.userloaded)
const ownerInfoLoaded = computed(() => store.getters['User/ownerInfoLoaded'])
const activeTeam = computed(() => store.state.User.activatedTeam)
const memberPermission = computed(() => activeTeam.value && store.state.User.memberPermission)
const ownerDetails = computed(() => store.state.User.ownerDetails || [])
const ownerMembers = computed(() => store.state.User.ownermember || {})

// Owner's email — used for the member "no access" contact link (NOT the current
// user's own email, which was the old bug).
const ownerEmail = ref('')

const hasTeam = computed(() =>
	(ownerDetails.value && ownerDetails.value.length > 0) ||
	(ownerMembers.value && ownerMembers.value.members && ownerMembers.value.members.length > 0),
)
const canViewTeam = computed(() => !activeTeam.value || (memberPermission.value && memberPermission.value.teamView))

const canUseInviteOthersFeature = computed(() => {
	try {
		if (memberPermission.value?.role === 'owner') return true
		if (memberPermission.value?.role !== 'admin') return false
		return !!memberPermission.value?.teamInviteUsers
	} catch {
		return false
	}
})
const canInvite = computed(() => !activeTeam.value || canUseInviteOthersFeature.value)
const atSeatLimit = computed(() => memberLimit.value > 0 && seatUsage.value.atLimit)
const seatLimit = computed(() => seatUsage.value.max || memberLimit.value)
// Seats count people. At the limit, a NEW person cannot be invited, but with
// several workspaces someone already on the team can still be added to this
// one at no cost, so the button stays usable and the sheet explains.
const inviteDisabled = computed(() => atSeatLimit.value && !hasMultipleProjects.value)
const inviteDisabledReason = computed(() =>
	inviteDisabled.value ? `You've used all ${seatLimit.value} seats on your plan.` : '',
)
const inviteTooltip = computed(() => {
	if (inviteDisabled.value) return inviteDisabledReason.value
	if (atSeatLimit.value) return `All ${seatLimit.value} seats are in use. You can still add people who are already on your team.`
	return ''
})

const isMemberView = computed(() => !!activeTeam.value)

// --- list filtering (by object, never index) --------------------------------
// Active / Paused describe the person's grant in THIS workspace; someone on the
// roster who is not on this workspace only appears under All (and Pending, when
// what they hold here is an invitation).
const filteredMembers = computed(() => {
	const query = search.value.toLowerCase()
	return (members.value || []).filter((member) => {
		const matchesSearch = !query
			|| (member.name && member.name.toLowerCase().includes(query))
			|| (member.email && member.email.toLowerCase().includes(query))
		if (!matchesSearch) return false
		const here = member.onProject !== false
		if (statusFilter.value === 'pending') return member.status === 'pending'
		if (statusFilter.value === 'active') return member.status !== 'pending' && here && member.enabled !== false
		if (statusFilter.value === 'paused') return member.status !== 'pending' && here && member.enabled === false
		return true
	})
})

// --- pagination (client-side; the full list is already loaded) ---------------
// Natural order is preserved (owner first, then members, then invites), so the
// owner simply lives on page 1. Desktop table and mobile cards share the slice.
const PAGE_SIZE = 10
const page = ref(1)
const totalFiltered = computed(() => filteredMembers.value.length)
const totalPages = computed(() => Math.max(1, Math.ceil(totalFiltered.value / PAGE_SIZE)))
const pagedMembers = computed(() => {
	const start = (page.value - 1) * PAGE_SIZE
	return filteredMembers.value.slice(start, start + PAGE_SIZE)
})
const rangeStart = computed(() => (totalFiltered.value ? (page.value - 1) * PAGE_SIZE + 1 : 0))
const rangeEnd = computed(() => Math.min(page.value * PAGE_SIZE, totalFiltered.value))
// Reset to page 1 whenever the search or status filter narrows the list.
watch([search, statusFilter], () => { page.value = 1 })
// Never strand the user past the last page after removals / filter changes.
watch(totalPages, (tp) => { if (page.value > tp) page.value = tp })

const usageChip = computed(() => (memberLimit.value > 0 ? {
	used: seatUsage.value.occupied,
	limit: memberLimit.value,
	label: 'seats',
	labelSingular: 'seat',
	planLabel: 'team seats',
} : null))

const showTable = computed(() => loaded.value && (members.value || []).length > 0)
const teamBodyReady = computed(() => ownerInfoLoaded.value || loaded.value)

// --- lifecycle ---------------------------------------------------------------
async function bootstrap() {
	teamnameDraft.value = ''
	await store.dispatch('User/getinfoOwner')
	reload()
	store.dispatch('Streams/getStreams').catch(() => {})
}

async function reload() {
	await load()
	nameDraft.value = teamName.value
	await resolveOwnerEmail()
}

async function resolveOwnerEmail() {
	if (ownerMembers.value?.owner?.email) {
		ownerEmail.value = ownerMembers.value.owner.email
		return
	}
	if (!activeTeam.value) {
		ownerEmail.value = store.getters['User/userEmail'] || ''
		return
	}
	try {
		const teamId = tid.value || ownerMembers.value?.id
		if (teamId) {
			const details = await UserService.getTeamOwner(teamId)
			ownerEmail.value = details?.owner?.email || ''
		}
	} catch {
		ownerEmail.value = ''
	}
}

let onTeamChanging
let onTeamChanged
onMounted(async () => {
	await bootstrap()
	onTeamChanging = () => {
		store.dispatch('Ui/setLoadingStatus', true)
	}
	onTeamChanged = async () => {
		await store.dispatch('User/getinfoOwner', true)
		await reload()
		store.dispatch('Ui/setLoadingStatus', false)
	}
	appEventBus.on(APP_EVENTS.TEAM_CHANGING, onTeamChanging)
	appEventBus.on(APP_EVENTS.TEAM_CHANGED, onTeamChanged)
})

onBeforeUnmount(() => {
	appEventBus.off(APP_EVENTS.TEAM_CHANGING, onTeamChanging)
	appEventBus.off(APP_EVENTS.TEAM_CHANGED, onTeamChanged)
})

// `activatedTeam` starts as "" and `loadUser` writes `null` for a solo owner:
// both mean "no team", and reloading on that non-change remounted the member
// list right after the first load (and lost any click that landed in between).
const activeTeamKey = computed(() => activeTeam.value || null)
watch(activeTeamKey, async (next, prev) => {
	if (next === prev) return
	await store.dispatch('User/getinfoOwner', true)
	await reload()
})
// Roles and access are per workspace, so moving between workspaces changes what
// every row says. The first resolution (null → project) is not a move — the
// mount already loaded this team — so it must not trigger a second load.
watch(activeProject, async (next, prev) => {
	if (!prev || next?.id === prev.id) return
	await reload()
})
watch(teamName, (val) => { if (!editingName.value) nameDraft.value = val })

// `/manage/team?invite=1` (the sidebar's "Invite members" shortcut) opens the
// invite sheet once the roster is in and the caller may invite here. Without a
// team yet the page is already showing the create step, which is the right
// first move. The flag is consumed so a reload does not reopen the sheet.
const inviteRequested = computed(() => loaded.value && String(route.query.invite || '') === '1')
watch(inviteRequested, (requested) => {
	if (!requested) return
	router.replace({ path: route.path, query: { ...route.query, invite: undefined } })
	if (hasTeam.value && canInvite.value && !inviteDisabled.value) openInvite()
}, { immediate: true })

// --- invite / edit / add -----------------------------------------------------
function openInvite() {
	modalMember.value = null
	modalPrefill.value = null
	nextTick(() => showModal('team-member-modal'))
}
function editMember(member) {
	modalMember.value = member
	modalPrefill.value = null
	nextTick(() => showModal('team-member-modal'))
}
// Someone already on the team, not yet on this workspace: same sheet, with the
// person filled in. The lookup then offers their other workspaces' permissions
// as a starting point.
function addMember(member) {
	modalMember.value = null
	modalPrefill.value = { name: member.name, email: member.email }
	nextTick(() => showModal('team-member-modal'))
}
async function onInviteSent() {
	await refreshInvited()
}
// An existing member was granted this workspace: their row changes (role,
// access, chips), so reload the roster rather than just the pending list.
// The roster is rebuilt from the team payload in the store, and the grant the
// server just created is only in the next copy of it — without the refetch the
// new workspace chip appeared only after a page reload.
async function onMemberAdded() {
	await store.dispatch('User/getinfoOwner', true)
	await reload()
}
function onUpdatePermissions(permissions) {
	const member = modalMember.value
	if (!member) return
	upsertMember({ ...member, role: permissions.role, permissions })
}

// --- resend ------------------------------------------------------------------
async function resendMember(member) {
	if (!member || !tid.value) return
	try {
		// Re-sending is the same call as inviting; bound to this workspace so it
		// refreshes THIS workspace's invitation rather than creating another.
		await UserService.inviteTeamadmin(tid.value, member.name, member.email, member.permissions, activeProject.value?.id)
		notify({ group: 'success', text: `Invitation resent to ${member.email}` })
		await refreshInvited()
	} catch (err) {
		// Backend dependency: resend relies on an invite upsert that exists on
		// staging but may not on the current API. Fail softly.
		notify({
			group: 'error',
			text: err?.message || 'Could not resend the invitation. Please try again later.',
		})
	}
}

// --- pause / enable (immediate; reversible; per project) --------------------
async function changeMemberStatus(member) {
	if (!member) return
	const endpoint = 'member'
	const mid = member._id || member.id
	permissionTogglestatus.value = true
	const nextEnabled = member.enabled === false // pausing false→true (enable), else disable
	try {
		// PUT .../permissions writes the grant in the workspace the page stands in.
		const change = await UserService.toggleMember(mid, tid.value, nextEnabled, endpoint)
		if (change?.error || change?.success === false) throw new Error(change?.message || 'Update failed')
		const resolved = change?.updatedPermission?.isEnabled ?? change?.isEnabled
		const enabled = resolved != null ? resolved : nextEnabled
		const workspaces = (member.workspaces || []).map((w) => (
			w.id === activeProject.value?.id ? { ...w, isEnabled: enabled } : w
		))
		upsertMember({ ...member, enabled, workspaces })
		const where = hasMultipleProjects.value && activeProject.value?.name ? ` in ${activeProject.value.name}` : ''
		notify({
			group: 'success',
			text: nextEnabled ? `${member.name} was enabled${where}` : `${member.name} was paused${where}`,
		})
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Could not update member. Please try again later.' })
	} finally {
		permissionTogglestatus.value = false
	}
}

// --- remove / cancel (destructive) ------------------------------------------
// The row menu only names the intent (`scope: 'project' | 'account'`); the
// dialog spells out what the server will do to the person's seat, and the
// mutation runs from its confirm.
function askRemove({ member, scope } = {}) {
	if (!member) return
	removeTarget.value = { member, scope: scope || 'account' }
	removeError.value = ''
	removeDialogOpen.value = true
}

async function confirmRemove({ member, scope }) {
	if (!member || removing.value) return
	const isPending = member.status === 'pending'
	const endpoint = isPending ? 'invitation' : 'member'
	const projectScoped = scope === 'project' && !!activeProject.value?.id
	// For an invitation, the id is the invitation INTO THIS PROJECT (the row's
	// primary); for a member it is the person.
	const mid = isPending ? (member.id || member._id) : (member._id || member.id)
	removing.value = true
	removeError.value = ''
	try {
		const res = await UserService.removeMember(mid, tid.value, endpoint, projectScoped ? activeProject.value.id : undefined)
		if (!res?.success) throw new Error(res?.message || 'Could not complete that action.')
		track(EVENTS.TEAM_MEMBER_REMOVED, {
			removal_type: isPending ? 'invitation' : 'member',
			scope: projectScoped ? 'project' : 'account',
			offboarded: !projectScoped || res.removedFromTeam !== false,
		})

		const projectLabel = activeProject.value?.name || 'this workspace'
		if (isPending) {
			if (projectScoped) {
				const remaining = (member.workspaces || []).filter((w) => w.id !== activeProject.value.id)
				if (remaining.length) {
					upsertMember({ ...member, workspaces: remaining, onProject: false })
				} else {
					removeById(mid)
				}
				notify({ group: 'success', text: remaining.length ? `Invitation to ${projectLabel} cancelled` : 'Invitation cancelled' })
			} else {
				removeById(mid)
				notify({ group: 'success', text: (member.workspaces || []).length > 1 ? 'All invitations cancelled' : 'Invitation cancelled' })
			}
		} else if (projectScoped) {
			applyProjectRemoval(member, res)
			notify({
				group: 'success',
				text: res.removedFromTeam === false
					? `${member.name} was removed from ${projectLabel}`
					: `${member.name} was removed from ${projectLabel}. That was their only workspace, so their seat is free again.`,
			})
		} else {
			removeById(mid)
			notify({ group: 'success', text: hasMultipleProjects.value ? `${member.name} was removed from the account` : `${member.name} removed` })
		}
		removeDialogOpen.value = false
		removeTarget.value = null
		// Seats are account-wide and count people, so any removal can free one.
		await refreshSeats()
	} catch (err) {
		removeError.value = err?.message || 'Could not complete that action. Please try again later.'
	} finally {
		removing.value = false
	}
}

// --- team create / rename ----------------------------------------------------
async function createTeam() {
	if (!teamnameDraft.value) return
	createError.value = ''
	try {
		store.dispatch('Ui/setLoadingStatus', true)
		const res = await UserService.setTeam(teamnameDraft.value)
		showCreateForm.value = false
		if (res) {
			await store.dispatch('User/getinfoOwner', true)
			await reload()
			track(EVENTS.TEAM_CREATED, { member_limit: memberLimit.value })
			notify({ group: 'success', text: 'Your team was created.' })
		}
	} catch (err) {
		// 409: the account already has its one team (it is account-wide, so it
		// exists in every project). Say so in the form, then show that team:
		// straight from `data.teamId` when the server names it, otherwise by
		// re-reading the owner's teams.
		if (err?.code === 409) {
			const message = err?.message || 'Your account already has a team.'
			createError.value = message
			const shown = await showExistingTeam(err?.data?.teamId)
			// The form is gone once the team is on screen; the reason should not be.
			if (shown) notify({ group: 'info', title: message, text: 'This is it. Invite people to your workspaces from here.' })
			return
		}
		createError.value = err?.message || 'Something went wrong. Please try again later.'
	} finally {
		store.dispatch('Ui/setLoadingStatus', false)
	}
}

/**
 * Loads the team a 409 pointed at. `/teams/info/{id}?role=true` carries the
 * same member shape as `/my-teams` (workspaces, activeGrant), so it can stand
 * in for the owner's team list directly. Resolves to whether a team is now on
 * screen.
 */
async function showExistingTeam(teamId) {
	if (teamId) {
		try {
			const team = await UserService.getTeamOwner(teamId)
			if (team && (team._id || team.id)) {
				store.commit('User/UPDATE_TEAMINFOOWNER', [{ ...team, _id: team._id || team.id }])
				store.commit('User/UPDATE_OWNER', [])
				await reload()
				return true
			}
		} catch (err) {
			// fall through to the list
		}
	}
	await store.dispatch('User/getinfoOwner', true)
	await reload()
	return hasTeam.value
}

function startEditName() {
	nameDraft.value = teamName.value
	editingName.value = true
}
function cancelEditName() {
	editingName.value = false
	nameDraft.value = teamName.value
}
async function commitName() {
	editingName.value = false
	const next = (nameDraft.value || '').trim()
	if (!next || next === teamName.value) {
		nameDraft.value = teamName.value
		return
	}
	try {
		const res = await UserService.changeTeamname(tid.value, next)
		if (res?.success) {
			teamName.value = next
			track(EVENTS.TEAM_RENAMED, {})
			notify({ group: 'success', text: 'Your team was renamed.' })
		}
	} catch (err) {
		nameDraft.value = teamName.value
		notify({ group: 'error', title: "Couldn't change team name", text: err?.message })
	}
}
</script>

<template>
	<div>
		<Spinner
			v-if="!userloaded"
			text="Retrieving data..."
			classes="text-dark-8 mh-60"
			spinner-color="var(--c-dark-8)"
			spinner-size="15px"
		/>

		<template v-else>
			<!-- Has access (owner, or member with teamView) -->
			<div v-if="canViewTeam">
				<PageHeader
					title="Team"
					:subtitle="headerSubtitle"
					mobile-inline
					:usage="loaded && hasTeam ? usageChip : null"
				>
					<template v-if="showProjectContext" #meta>
						<span
							class="inline-flex items-center gap-1.5 rounded-full bg-surface-3 pl-1 pr-2.5 py-0.5 text-tiny text-surface-text-1"
							:title="`Showing roles and access for the ${activeProject.name} workspace`"
							data-testid="team-project-context"
						>
							<ProjectTile :name="activeProject.name" :color="activeProject.color" size="size-5" textClass="text-[9px]" rounded="rounded-full" />
							Access in {{ activeProject.name }}
						</span>
					</template>
					<Tooltip v-if="hasTeam && canInvite" :active="!!inviteTooltip" :content="inviteTooltip" placement="bottom" wrap-trigger wrapper-class="inline-flex">
						<FormButton type="main" size="md" class="gap-1.5" :disabled="inviteDisabled" @click="openInvite">
							<IconBase><IconPlus /></IconBase>
							Invite member
						</FormButton>
					</Tooltip>
				</PageHeader>

				<!-- With a team: member list -->
				<div v-if="hasTeam">
					<!-- Team header: eyebrow + prominent name + rename, seats beneath -->
					<div class="mb-4 min-w-0">
						<p class="text-tiny uppercase tracking-wider text-surface-6 mb-1">Team Name</p>
						<div class="flex items-center gap-2 min-w-0">
							<template v-if="editingName">
								<FormInput
									v-model="nameDraft"
									size="md"
									class="max-w-xs"
									aria-label="Team name"
									@keydown.enter="commitName"
									@keydown.esc="cancelEditName"
								/>
								<FormButton type="main" size="sm" @click="commitName">Save</FormButton>
								<FormButton type="link" size="sm" @click="cancelEditName">Cancel</FormButton>
							</template>
							<template v-else>
								<h2 class="text-lg font-semibold text-surface-text-2 truncate mb-0" :title="teamName">{{ teamName || 'Your team' }}</h2>
								<FormButton
									v-if="!isMemberView"
									type="secondary"
									isIcon
									size="sm"
									aria-label="Rename team"
									class="shrink-0 !text-surface-8 hover:!text-white"
									@click="startEditName"
								>
									<IconBase class="w-4 h-4"><IconEditPencil /></IconBase>
								</FormButton>
							</template>
						</div>
						<!-- The name is the account's, not this workspace's; worth saying only
						once there are other workspaces it could be confused with. -->
						<p v-if="showProjectContext" class="text-tiny text-surface-6 mt-1 mb-0" data-testid="team-name-scope">
							Team name is shared across all your workspaces.
						</p>

						<TeamSeatUsage v-if="memberLimit" :usage="seatUsage" :multi-project="hasMultipleProjects" class="mt-2" />
					</div>

					<!-- Contained panel: controls + member list -->
					<div class="bg-surface-2 rounded-xl overflow-hidden">
					<!-- Controls: search + status filter -->
					<div class="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-surface-3">
						<div class="w-full lg:max-w-sm flex items-center relative">
							<IconBase class="text-surface-8 absolute left-4 shrink-0 z-10 pointer-events-none">
								<icon-search class="w-4 h-4" />
							</IconBase>
							<FormInput customClasses="!pl-10 !bg-surface-1" v-model="search" placeholder="Search by name or email" />
							<FormButton v-if="search" class="absolute right-2 shrink-0 z-10" type="link" isIcon size="sm" @click="search = ''">
								<icon-base class="w-4 h-4" fill="none"><icon-cross class="w-4 h-4" /></icon-base>
							</FormButton>
						</div>
						<TabGroup type="segmented" size="sm" class="shrink-0 lg:ml-auto">
							<TabItem :is-active="statusFilter === 'all'" @click="statusFilter = 'all'">All</TabItem>
							<TabItem :is-active="statusFilter === 'active'" @click="statusFilter = 'active'">Active</TabItem>
							<TabItem :is-active="statusFilter === 'pending'" @click="statusFilter = 'pending'">Pending</TabItem>
							<TabItem :is-active="statusFilter === 'paused'" @click="statusFilter = 'paused'">Paused</TabItem>
						</TabGroup>
					</div>

					<!-- Only the first load gets a spinner: a reload keeps the list mounted
					and lets rows update in place. -->
					<Spinner
						v-if="!members.length && membersLoading"
						text="Loading members..."
						classes="text-dark-8 mh-60"
						spinner-color="var(--c-dark-8)"
						spinner-size="15px"
					/>
					<TeamTable
						v-else
						:table-data="pagedMembers"
						:permission-togglestatus="permissionTogglestatus"
						:active-team="activeTeam"
						:active-project="activeProject"
						:show-projects="hasMultipleProjects"
						@edit-member="editMember"
						@add-member="addMember"
						@resend-member="resendMember"
						@change-member-status="changeMemberStatus"
						@remove-member="askRemove"
					/>

					<!-- Pager: quiet footer inside the panel, shared by table + cards -->
					<div
						v-if="showTable && totalFiltered"
						class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-white/[0.06]"
					>
						<p class="text-tiny text-surface-6 mb-0 tabular-nums">{{ rangeStart }}–{{ rangeEnd }} of {{ totalFiltered }}</p>
						<div v-if="totalPages > 1" class="flex items-center gap-2">
							<FormButton type="secondary" size="sm" :disabled="page <= 1" @click="page--">Prev</FormButton>
							<span class="text-tiny text-surface-6 tabular-nums">Page {{ page }} of {{ totalPages }}</span>
							<FormButton type="secondary" size="sm" :disabled="page >= totalPages" @click="page++">Next</FormButton>
						</div>
					</div>
					</div>
				</div>

				<Spinner
					v-else-if="!teamBodyReady"
					text="Loading team..."
					classes="text-dark-8 mh-60"
					spinner-color="var(--c-dark-8)"
					spinner-size="15px"
				/>

				<!-- No team yet: onboarding -->
				<EmptyState v-else min-height="55vh">
					<template #icon>
						<IconBase :width="28" :height="28" fill="none">
							<IconTeam />
						</IconBase>
					</template>
					<template #title>Teamwork begins here</template>
					<template #subtitle>
						Invite Admins and Moderators, assign exactly what they can do, and stay in control of your account.
					</template>
					<template #actions>
						<template v-if="teamAllowed">
							<div v-if="!showCreateForm" class="flex flex-col items-center gap-2">
								<FormButton type="main" size="lg" @click="showCreateForm = true">Create Team</FormButton>
							</div>
							<div v-else class="w-full max-w-sm text-left bg-surface-2 rounded-xl p-4">
								<h4 class="mb-1 text-base font-medium text-surface-text-2">What's your team name?</h4>
								<p class="mb-3 text-tiny text-surface-7">
									Usually your company or studio. One team covers your whole account; you choose who can access each workspace.
								</p>
								<FormInput size="md" class="mb-3" v-model="teamnameDraft" placeholder="e.g. Zishan Productions" :is-error="!!createError" @keydown.enter="createTeam" />
								<Alert v-if="createError" type="error" class="mb-3" :text="createError" data-testid="team-create-error" />
								<div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
									<FormButton size="md" type="secondary" class="w-full sm:w-auto justify-center" @click="showCreateForm = false; teamnameDraft = ''; createError = ''">Cancel</FormButton>
									<FormButton size="md" type="main" class="w-full sm:w-auto justify-center" :disabled="!teamnameDraft" @click="createTeam">Save</FormButton>
								</div>
							</div>
						</template>
						<div v-else class="w-full max-w-md bg-surface-2 py-4 px-4 rounded-xl text-center">
							<h4 class="text-base font-medium text-surface-text-2 mb-2">Upgrade to use this feature</h4>
							<p class="text-surface-7 text-sm mb-4">Your plan doesn't allow creating a team.</p>
							<FormButton target="_blank" type="main" size="md" href="/app/subscribe">See Pricing Plans</FormButton>
						</div>
					</template>
				</EmptyState>
			</div>

			<!-- No access: member without teamView → contact the OWNER -->
			<div v-else class="w-full">
				<PageHeader title="Team" />
				<EmptyState min-height="55vh" title="You don't have permission to access this.">
					<template #subtitle>
						Contact the team owner
						<a v-if="ownerEmail" class="text-surface-8 underline" :href="'mailto:' + ownerEmail">{{ ownerEmail }}</a>
						<span v-else>the team owner</span>
						to request access.
					</template>
				</EmptyState>
			</div>
		</template>

		<!-- Invite / edit / add side-sheet -->
		<TeamMemberModal
			:team-id="tid"
			:members="members"
			:member="modalMember"
			:team-name="teamName"
			:active-project="activeProject"
			:prefill="modalPrefill"
			:at-seat-limit="atSeatLimit"
			:seat-limit="seatLimit"
			@invite-sent="onInviteSent"
			@member-added="onMemberAdded"
			@update-permissions="onUpdatePermissions"
		/>

		<!-- Remove / cancel confirmation, honest about seats -->
		<TeamRemoveDialog
			v-model="removeDialogOpen"
			:member="removeTarget?.member || null"
			:scope="removeTarget?.scope || 'account'"
			:active-project="activeProject"
			:multi-project="hasMultipleProjects"
			:busy="removing"
			:error="removeError"
			@confirm="confirmRemove"
		/>
	</div>
</template>

<style scoped>
.mh-60 {
	min-height: 60vh;
}
</style>
